import React from 'react';
import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";

const ValorisationGuide = () => {
  const wasteCategories = [
    {
      title: "Métaux Ferreux",
      icon: "🔩",
      description: "Fer, acier, fonte - Valorisation à 95%",
      link: "/valorisation/metaux-ferreux",
      color: "fabre-green"
    },
    {
      title: "Métaux Non-Ferreux", 
      icon: "🥉",
      description: "Aluminium, cuivre, zinc - Haute valeur",
      link: "/valorisation/metaux-non-ferreux",
      color: "fabre-teal"
    },
    {
      title: "DEEE",
      icon: "📱",
      description: "Équipements électriques et électroniques",
      link: "/valorisation/deee",
      color: "fabre-darkgreen"
    },
    {
      title: "Papiers-Cartons",
      icon: "📦",
      description: "Recyclage en nouvelles fibres",
      link: "/valorisation/papiers-cartons", 
      color: "fabre-green"
    },
    {
      title: "DIB",
      icon: "🗂️",
      description: "Déchets industriels banals",
      link: "/valorisation/dib",
      color: "fabre-teal"
    },
    {
      title: "Huiles Minérales",
      icon: "🛢️",
      description: "Huiles usagées industrielles et automobiles",
      link: "/services/huiles-minerales",
      color: "fabre-darkgreen"
    },
    {
      title: "Huiles Alimentaires",
      icon: "🫒",
      description: "Huiles de friture et graisses alimentaires",
      link: "/services/huiles-alimentaires", 
      color: "fabre-green"
    }
  ];

  const process = [
    {
      step: "1",
      title: "Collecte",
      description: "Ramassage sélectif par nos équipes spécialisées"
    },
    {
      step: "2", 
      title: "Tri",
      description: "Séparation et classification selon les matériaux"
    },
    {
      step: "3",
      title: "Traitement",
      description: "Transformation et préparation pour recyclage"
    },
    {
      step: "4",
      title: "Valorisation",
      description: "Réintégration dans les circuits économiques"
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Guide de la Valorisation des Déchets | Processus de Recyclage Occitanie"
        description="Comprendre la valorisation des déchets : métaux, DEEE, papiers-cartons, DIB. Processus, techniques et bénéfices environnementaux en Occitanie."
        canonical="/guide-valorisation"
      />
      
      <Navbar />
      
      <main className="flex-grow pt-20">
        {/* Hero */}
        <section className="bg-gradient-to-br from-fabre-green via-fabre-teal to-fabre-darkgreen text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
                Guide de la Valorisation
              </h1>
              <p className="text-xl leading-relaxed mb-8 opacity-90">
                Découvrez comment vos déchets deviennent de nouvelles ressources grâce à nos processus de valorisation
              </p>
            </div>
          </div>
        </section>

        {/* Qu'est-ce que la valorisation */}
        <section className="py-20 bg-gradient-to-br from-white to-fabre-lightgreen/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-heading font-bold text-center text-fabre-gray mb-12">
                Qu'est-ce que la Valorisation ?
              </h2>
              <div className="bg-white rounded-xl p-8 shadow-lg border border-fabre-green/10">
                <p className="text-lg text-fabre-darkteal leading-relaxed mb-6">
                  La valorisation des déchets consiste à leur donner une seconde vie en les transformant 
                  en nouvelles matières premières ou en sources d'énergie. C'est le cœur de l'économie circulaire.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4">
                    <div className="text-3xl mb-3">♻️</div>
                    <h3 className="font-bold text-fabre-darkgreen mb-2">Recyclage Matière</h3>
                    <p className="text-sm text-fabre-darkteal">Transformation en nouvelles matières premières</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="text-3xl mb-3">⚡</div>
                    <h3 className="font-bold text-fabre-darkgreen mb-2">Valorisation Énergétique</h3>
                    <p className="text-sm text-fabre-darkteal">Production d'énergie à partir des déchets</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="text-3xl mb-3">🌱</div>
                    <h3 className="font-bold text-fabre-darkgreen mb-2">Valorisation Organique</h3>
                    <p className="text-sm text-fabre-darkteal">Compostage et méthanisation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Processus de valorisation */}
        <section className="py-20 bg-gradient-to-br from-fabre-lightgray/30 to-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-heading font-bold text-center text-fabre-gray mb-16">
              Notre Processus de Valorisation
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {process.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                    <div className="w-12 h-12 bg-fabre-green text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold text-fabre-darkgreen mb-3">
                      {item.title}
                    </h3>
                    <p className="text-fabre-darkteal text-sm">
                      {item.description}
                    </p>
                  </div>
                  {index < process.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 text-fabre-green text-2xl">
                      →
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Types de déchets valorisables */}
        <section className="py-20 bg-gradient-to-r from-fabre-green/10 to-fabre-teal/10">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-heading font-bold text-center text-fabre-gray mb-16">
              Types de Déchets Valorisables
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {wasteCategories.map((category, index) => (
                <Link 
                  key={index}
                  to={category.link}
                  className="group bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-4xl mb-4">{category.icon}</div>
                  <h3 className={`text-xl font-bold text-${category.color} mb-3 group-hover:text-fabre-darkgreen transition-colors`}>
                    {category.title}
                  </h3>
                  <p className="text-fabre-darkteal text-sm mb-4">
                    {category.description}
                  </p>
                  <div className="text-fabre-green font-medium group-hover:underline">
                    En savoir plus →
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Bénéfices environnementaux */}
        <section className="py-20 bg-gradient-to-br from-white to-fabre-lightgreen/10">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-heading font-bold text-center text-fabre-gray mb-16">
              Bénéfices Environnementaux
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-fabre-darkgreen mb-6">
                  🌍 Impact Environnemental
                </h3>
                <ul className="space-y-3 text-fabre-darkteal">
                  <li className="flex items-start gap-3">
                    <span className="text-fabre-green">•</span>
                    <span>Réduction de 75% des émissions de CO₂</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-fabre-green">•</span>
                    <span>Économie des ressources naturelles</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-fabre-green">•</span>
                    <span>Diminution des déchets en décharge</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-fabre-green">•</span>
                    <span>Protection des écosystèmes</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-fabre-darkgreen mb-6">
                  💰 Bénéfices Économiques
                </h3>
                <ul className="space-y-3 text-fabre-darkteal">
                  <li className="flex items-start gap-3">
                    <span className="text-fabre-teal">•</span>
                    <span>Création d'emplois locaux</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-fabre-teal">•</span>
                    <span>Réduction des coûts d'enfouissement</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-fabre-teal">•</span>
                    <span>Génération de revenus</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-fabre-teal">•</span>
                    <span>Indépendance matières premières</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-20 bg-gradient-to-r from-fabre-green via-fabre-teal to-fabre-darkgreen text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-heading font-bold mb-8">
              Valorisez vos Déchets avec Nous
            </h2>
            <p className="text-xl mb-12 opacity-90 max-w-3xl mx-auto">
              Rejoignez l'économie circulaire et donnez une seconde vie à vos déchets 
              tout en contribuant à la protection de l'environnement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="bg-white text-fabre-green hover:bg-fabre-lightgreen hover:text-white px-8 py-4 rounded-lg font-medium transition-colors"
              >
                Demander un devis
              </Link>
              <Link 
                to="/estimation-metaux"
                className="border-2 border-white text-white hover:bg-white hover:text-fabre-green px-8 py-4 rounded-lg font-medium transition-colors"
              >
                Estimer mes métaux
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ValorisationGuide;